import { Product as ProductHelper } from '../../core';
import { SalesTermsMapper } from '../../mappers';
import {
  BillOrderAttachmentType,
  BillSettlementLineItem,
  BillStatus,
  BillType,
  BusinessEntity,
  Contact,
  CustomFields,
  ListTemplate,
  Note,
  Organization,
  Payment,
  Place,
  SalesTerms,
  Shipment,
} from '../../types';
import { USD } from '../../types/usd';
import Money from '../../utils/Money';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { documentNotes } from '../components/documentNotes.template';
import { OrganizationComponent, OrganizationTemplateType } from '../components/organization.template';
import { ShipmentComponent, ShipmentTemplateType } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import fromDecimal from '../utils/fromDecimal';

import { applyTableMetadata, formatCurrency, formatDate, formatDecimal, getPrimaryBusiness } from './formatters';

export type BillSettlementInputType = {
  id: number;
  slug: string;
  type: BillType;

  // Organization
  organization: Organization;

  // Details
  status: BillStatus;
  reference?: string;
  paymentTerms: SalesTerms;

  // Counter party
  businessEntity?: BusinessEntity;
  contact?: Contact;
  billingAddress?: Place;

  invoiceDate?: string | Date;
  dueDate?: string | Date;

  // Commission
  consignmentCommissionRate?: number;
  consignmentCommissionAmount?: number;

  receipts: Payment[];
  adjustments: Array<{ id: number; amount: number; memo: string; reason: string }>;
  lineItems: BillSettlementLineItem[];

  attachedOrders: { type: BillOrderAttachmentType; growerProduct: AttachedGrowerProduct }[];

  notes: Note[];
  customFields?: CustomFields;
};

type AttachedGrowerProduct = {
  id: number;
  slug?: string;
  settlementBasis?: string;
  shipment: Shipment;
  harvestedAt?: string | Date;
  fieldTicketId?: string;
  harvestLocation?: string;
  customFields?: CustomFields;
};

export type BillSettlementTemplateDataType = Partial<{
  billNumber: string;
  orgLogo: string;
  organization: OrganizationTemplateType;
  businessAddress: AddressTemplateType;
  phoneNumber: string;
  email: string;
  payTo: string;
  billingAddress: AddressTemplateType;
  lineItems: ListTemplate<{
    description?: string;
    unitCount?: string;
    unitPrice?: string;
    subtotal?: string;
    category?: string;
  }>;
  totalAmountDue: string;
  totalPaid: string;
  invoiceDate: string;
  dueDate: string;
  dueDateVerbose: string;
  reference: string;
  paymentTerms: string;
  orderLabel: string;
  salesPerson: string;
  customerPo: string;
  shipment: ShipmentTemplateType;
  notes: ListTemplate<{ text: string }>;
}>;

export const BillSettlementTemplate: DocumentTypePopulator<
  BillSettlementInputType,
  { productDescriptionTemplate?: string },
  BillSettlementTemplateDataType
> = {
  populate: (bill, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!bill) {
      return {};
    }

    const { slug, receipts, invoiceDate, reference, paymentTerms, billingAddress, organization, lineItems, notes } =
      bill;

    const totalPaid = receipts.reduce((acc, payment) => acc + payment.paymentAmount || 0, 0);
    const growerProduct = bill.attachedOrders.find(
      (ao) => ao.growerProduct && ao.type === 'CONSIGNMENT'
    )?.growerProduct;

    if (!growerProduct) {
      return {};
    }

    const orderLabel = `GP #${growerProduct.slug}`;

    const soPrefix = organization.salesOrderDocumentPrefix ?? 'SO';
    const settlementBasis = growerProduct?.settlementBasis;

    const salesLineItems = lineItems
      .filter((lineItem) => !!lineItem.sourceLineItem?.lot)
      .map((lineItem) => {
        const sourceLineItem = lineItem.sourceLineItem;
        const lot = lineItem.sourceLineItem!.lot;

        const sources = lot.orderSources ?? [];

        const proporitionOfSource = sources?.find(
          (source) => source.workOrderId === growerProduct.id
        )?.proportionComposedOfSource;

        const proportionOfSale = +(fromDecimal(proporitionOfSource) ?? 0).toFixed(4);
        const { unitsPicked } = sourceLineItem!;

        let price;
        const weightComp = ProductHelper.Weight.of(sourceLineItem!.lot.product!)
          .withLot(sourceLineItem!.lot)
          .withLineItemPalletTags(sourceLineItem!.palletTagsOnLineItem);

        if (settlementBasis === 'WEIGHT') {
          price = lineItem.consignmentWeightUnitPrice;
        } else {
          price = lineItem.unitPrice;
        }

        price = USD.fromCents(price ?? 0, 3).cents;

        const settlementFactor =
          settlementBasis === 'WEIGHT' ? weightComp.forQuantity(unitsPicked) : (unitsPicked ?? 0);

        const total = Math.round((price ?? 0) * settlementFactor * proportionOfSale);

        const weightScalar = settlementBasis === 'WEIGHT' ? (proportionOfSale ?? 1.0) : 1.0;
        const unitCountScalar = settlementBasis === 'UNITS' ? (proportionOfSale ?? 1.0) : 1.0;

        return {
          settlementPrice: price,
          unitCount: (sourceLineItem?.unitsPicked ?? 0) * unitCountScalar,
          subtotal: total,
          weight: weightComp.forQuantity(unitsPicked) * weightScalar,
          salesOrder: `${soPrefix} #${sourceLineItem!.salesOrder.slug}`,
          shipDate: sourceLineItem?.salesOrder.shipDate,
          product: ProductHelper.productDescription(
            sourceLineItem!.lot.product,
            productDescriptionTemplate ?? organization.productDescriptionTemplate
          ),
        };
      });

    const deductions = lineItems
      .filter((lineItem) => !!lineItem.sourceExpense)
      .map((lineItem) => {
        const fallbackTotal = lineItem.sourceExpense!.unitAmount * lineItem.sourceExpense!.quantity;

        return {
          description: lineItem.sourceExpense!.description,
          category: lineItem.sourceExpense!.expenseType?.name ?? '',
          subtotal: lineItem.sourceExpense!.settlementAmount ?? fallbackTotal,
        };
      });

    const totalDeductions = deductions.reduce((total, deduction) => total + deduction.subtotal, 0);
    const totalSales = salesLineItems.reduce((total, billLineItem) => total + billLineItem.subtotal, 0);
    const totalWeight = salesLineItems.reduce((total, billLineItem) => total + billLineItem.weight, 0);
    const totalUnits = salesLineItems.reduce((total, billLineItem) => total + billLineItem.unitCount, 0);

    const deductionsTable = applyTableMetadata(
      deductions.map((deduction) => ({
        description: deduction.description,
        category: deduction.category,
        subtotal: formatCurrency(Money.toDollars(deduction.subtotal)),
      }))
    );

    const salesTable = applyTableMetadata(
      salesLineItems.map((billLineItem) => ({
        orderNumber: billLineItem.salesOrder,
        product: billLineItem.product,
        shipDate: formatDate(billLineItem.shipDate),
        weight: formatDecimal(billLineItem.weight, 4),
        quantity: formatDecimal(billLineItem.unitCount, 4),
        salesPrice: formatCurrency(billLineItem.settlementPrice / 100, 'USD', 3),
        subtotal: formatCurrency(Money.toDollars(billLineItem.subtotal)),
      }))
    );

    const commissionRate = bill.consignmentCommissionRate ?? 0;
    const commissionAmount = bill.consignmentCommissionAmount;

    const commission = commissionAmount !== null ? commissionAmount : totalSales * commissionRate;

    return {
      billNumber: slug,

      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),

      email: globalOptions?._settings?.overrideEmail,

      businessAddress: AddressComponent.templatize(getPrimaryBusiness(organization)),
      phoneNumber: getPrimaryBusiness(organization)?.phone,
      payTo: bill.businessEntity?.name,

      billingAddress: AddressComponent.templatize(billingAddress),
      shipment: ShipmentComponent.templatize(growerProduct.shipment),
      fieldTicketId: growerProduct.fieldTicketId,
      harvestDate: formatDate(growerProduct.harvestedAt),
      harvestLocation: growerProduct.harvestLocation,
      orderCustomFields: growerProduct.customFields,

      sales: salesTable,
      deductions: deductionsTable,
      totalWeight: formatDecimal(totalWeight),
      totalUnits: formatDecimal(totalUnits),
      totalDeductions: formatCurrency(Money.toDollars(totalDeductions)),
      totalSales: formatCurrency(Money.toDollars(totalSales)),
      totalPaid: formatCurrency(Money.toDollars(totalPaid)),
      invoiceDate: formatDate(invoiceDate),
      settlementBasis,
      commission: formatCurrency(Money.toDollars(commission ?? 0)),
      commissionRate: formatDecimal(commissionRate),
      commissionAmount: formatCurrency(Money.toDollars(commissionAmount ?? 0)),
      netReturns: formatCurrency(Money.toDollars(totalSales - totalDeductions - (commission ?? 0))),
      reference,
      paymentTerms: SalesTermsMapper.getLabel(paymentTerms),

      orderLabel,
      notes: documentNotes(notes, templateId, type),
      customFields: bill.customFields,
    };
  },
};
